<template>
  <v-container fluid class="pa-0 mt-n4">
    <DataTable
      :loading="loading"
      :items="supplierSummaries"
      :headers="tractSummaryHeaders"
      :customCells="customCells"
      :enableExternalSearch="true"
      :showSearch="false"
      :externalSearch="search"
      @filtered-items="filteredSuppliersChanged"
      resize
      :tableHeightCalculationData="{ offset: 500 }"
    >
    <template #rpi="{item}">
      <span> {{parseFloat(item.rpi) > 0 ? item.rpi.toFixed(2) : ''}} </span>
    </template>
    <template #dbh="{item}">
      <span> {{parseInt(item.dbh) > 0 ? Math.round(item.dbh) : ''}} </span>
    </template>
    <template #age="{item}">
      <span> {{parseInt(item.age) > 0 ? Math.round(item.age) : ''}} </span>
    </template>
    <template #plf="{item}">
      <span> {{parseFloat(item.plf) > 0 ? item.plf.toFixed(2) : ''}} </span>
    </template>
    <template #actions="{item}">
      <Icon
      icon="mdi-information-outline"
      :dataTestId="`product-${item.name}`"
      :small="false"
      :tooltipText="$t('viewDetailedBreakdown')"
      @icon-clicked="openDetailDialog(item)"/>
    </template>
      <template #body.append>
        <tr class="font-weight-bold">
          <td :class="col.class" v-for="col in summariesBySupplierCols" :key="col.id">
            <v-tooltip bottom color="secondary" v-if="col.tooltip">
              <template #activator="{ on }">
                <span v-on="on">{{ col.value }}</span>
              </template>
              <span class="subtitle-1 white--text">{{ col.tooltip }}</span>
            </v-tooltip>
            <span v-else>{{ col.value }}</span>
          </td>
        </tr>
      </template>
    </DataTable>
    <Dialog :stateId="dialogId" @dialog-closing="resetDialogs">
      <SupplierTractsDetail
        v-if="showSummaryDialog"
        :dateRange="dateRange"
        :tracts="focusedSupplierSummary.tracts"
        :supplier="focusedSupplierSummary.supplier"
        :entity="entity"
        :chosenCategory="chosenCategory"
        :speciesMode="speciesMode"
      />
    </Dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatMoney, tonStringFromPounds } from '@/utils/NumericMutations.js'
import { getDateRangeForPeriod } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { getAggregatedValueForHeader, getAggregateTooltip } from '@/headers/HeaderHelpers'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'DeliveredCostsBySupplier',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    SupplierTractsDetail: () => import('@/components/tract/tract-analysis/SupplierTractsDetail.vue')
  },

  props: {
    supplierSummaries: {
      type: Array,
      required: true
    },
    entity: {
      type: Object,
      required: false,
      default: undefined
    },
    dateRange: {
      type: Object,
      required: false,
      default: undefined
    },
    search: {
      type: String,
      default: ''
    },
    columnSet: {
      type: Object,
      required: true
    },
    chosenCategory: {
      type: Number,
      required: true
    },
    speciesMode: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dialogId: uniqueDialogId('delivered-costs-by-supplier'),
    selectedEntity: undefined,
    focusedSupplierSummary: null,
    showSummaryDialog: false,
    filteredSupplierSummaries: undefined
  }),

  computed: {
    ...mapGetters('user', ['businessEntities']),

    loading () {
      return !this.entity || !this.dateRange || !this.supplierSummaries
    },

    customCells () {
      return [
        {
          slotName: 'rpi',
          value: 'rpi'
        },
        {
          slotName: 'dbh',
          value: 'dbh'
        },
        {
          slotName: 'age',
          value: 'age'
        },
        {
          slotName: 'plf',
          value: 'plf'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    },

    tractSummaryHeaders () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    summariesBySupplierCols () {
      const moreThanOneTract = this.supplierSummaries.length > 1 && (!this.filteredSupplierSummaries || this.filteredSupplierSummaries.length > 1)
      if (moreThanOneTract) {
        return this.tractSummaryHeaders.map(this.tractHeaderToSummaryMapper)
      } else {
        return []
      }
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    tonStringFromPounds,
    getDateRangeForPeriod,

    openDetailDialog (supplier) {
      this.focusedSupplierSummary = supplier
      this.showSummaryDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.showSummaryDialog = false
      this.focusedSupplierSummary = null
    },

    tractHeaderToSummaryMapper (header) {
      const result = {
        id: header.value,
        class: `text-${header.align ?? 'left'}`,
        tooltip: getAggregateTooltip(header.aggregateType, header.weightedBy),
        value: '-'
      }

      if (header.value === 'supplier') {
        result.value = this.$t('totals')
        return result
      }

      this.getAggregateValues(header, result)
      return result
    },

    getAggregateValues (header, result) {
      if (!header.aggregateType) {
        result.value = '-'
        return
      }
      const supplierSummaries = this.getSupplierSummariesMapped()
      const value = getAggregatedValueForHeader(supplierSummaries, header)
      if (value === 0) {
        result.value = ''
      } else {
        result.value = header.format && header.aggregateType ? header.format(value) : value
      }
    },

    getSupplierSummariesMapped () {
      let supplierSummaries = this.supplierSummaries
      if (this.filteredSupplierSummaries) supplierSummaries = this.filteredSupplierSummaries

      supplierSummaries = cloneDeep(supplierSummaries)
      supplierSummaries = supplierSummaries.map(ts => ({
        ...ts,
        averageWeight: (ts.netWeight) / ts.loads,
        productionCostPerTon: ts.productionCost / (ts.netWeight / 2000),
        stumpageCostPerTon: ts.stumpageCost / (ts.netWeight / 2000)
      }))
      return supplierSummaries
    },

    filteredSuppliersChanged (filteredTracts) {
      this.filteredSupplierSummaries = filteredTracts
    }
  }
}
</script>
